import { memo, useMemo } from 'react';
import type { ChatElementInspectionPresentationPayload } from '@/jsb';
import type { AssistantState } from '../../types/runtime-state';
import {
  isAssistantMessage,
  isAppRequestMessage,
  isCompactedContextMessage,
  isErrorMessage,
  isMessageStartMessage,
  isQuestionMessage,
  isSaveFileReplyMessage,
  isSaveFileRequestMessage,
  isSandboxReviewMessage,
  isScheduleMessage,
  isSecretInputMessage,
  isToolMessage,
  isUserMessage,
  type ConversationAttachmentImageLayoutHandler,
  type ConversationMessage,
} from '../../types/conversation-message';
import { cn } from '@/utils/cn';
import { ErrorBoundary } from '../error-boundary';
import { AssistantMessage } from './assistant-message';
import {
  getActiveAssistantTurn,
  groupConversationTimelineItems,
  shouldHideAssistantOperations,
  type ConversationTimelineItem,
  type ConversationMessageGroup,
} from './assistant-turn';
import type { ToolMessageRenderer } from './assistant-message';
import type { AssistantBubbleStyle } from './assistant-message';
import type {
  ComputerUseStartDecisionRequest,
  HostAccessDecisionRequest,
} from './assistant-message';
import { UserMessage } from './user-message';
import { DebugMessage } from '@/utils/debug-message';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function metadataString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined;
}

function userMessageClientRequestId(
  message: ConversationMessage
): string | undefined {
  const metadata = message.metadata;
  if (!isRecord(metadata)) return undefined;
  const client = isRecord(metadata.client) ? metadata.client : null;
  return (
    metadataString(metadata.client_request_id) ??
    metadataString(metadata.clientRequestId) ??
    metadataString(client?.request_id) ??
    metadataString(client?.client_request_id) ??
    metadataString(client?.clientRequestId)
  );
}

function userMessageGroupKey(message: ConversationMessage): string {
  const clientRequestId = userMessageClientRequestId(message);
  return clientRequestId
    ? `user-client-request-${clientRequestId}`
    : message.id;
}

function isOptimisticLocalUserMessage(message: ConversationMessage): boolean {
  return isUserMessage(message) && message.id.startsWith('local-');
}

export function useConversationMessageGroups({
  messages,
  assistantStateSequence,
}: {
  messages: ConversationMessage[];
  assistantStateSequence: AssistantState[];
}) {
  const sortedAssistantStateSequence = useMemo(
    () =>
      [...assistantStateSequence].sort((a, b) => {
        if (a.sequence !== b.sequence) {
          return a.sequence - b.sequence;
        }
        return a.phaseStartedAt - b.phaseStartedAt;
      }),
    [assistantStateSequence]
  );

  const currentAssistantState =
    sortedAssistantStateSequence[sortedAssistantStateSequence.length - 1] ??
    null;

  const allMessages = useMemo(() => {
    const nextMessages: ConversationMessage[] = [];
    const anchoredMessageIds = new Set<string>();
    const assistantMessagesById = new Map<string, ConversationMessage>();

    for (const message of messages) {
      if (isAssistantMessage(message) && message.id) {
        assistantMessagesById.set(message.id, message);
      }
    }

    for (const message of messages) {
      if (isMessageStartMessage(message)) {
        const targetMessageId = message.messageId;
        if (!targetMessageId || anchoredMessageIds.has(targetMessageId)) {
          continue;
        }

        anchoredMessageIds.add(targetMessageId);

        const finalMessage = assistantMessagesById.get(targetMessageId);
        if (finalMessage) {
          nextMessages.push({
            ...finalMessage,
            timestamp: message.timestamp,
          });
          continue;
        }

        continue;
      }

      if (
        isAssistantMessage(message) &&
        message.id &&
        anchoredMessageIds.has(message.id)
      ) {
        continue;
      }

      nextMessages.push(message);
    }

    return nextMessages;
  }, [messages]);

  const groupedMessages = useMemo(() => {
    const items: ConversationTimelineItem[] = [];

    for (const message of allMessages) {
      if (isUserMessage(message) && isCompactedContextMessage(message)) {
        continue;
      }
      if (
        isUserMessage(message) ||
        isAssistantMessage(message) ||
        isErrorMessage(message) ||
        isSandboxReviewMessage(message) ||
        isQuestionMessage(message) ||
        isScheduleMessage(message) ||
        isSaveFileReplyMessage(message) ||
        isSaveFileRequestMessage(message) ||
        isAppRequestMessage(message) ||
        isSecretInputMessage(message) ||
        isToolMessage(message)
      ) {
        items.push({ type: 'message', message });
      }
    }

    for (const state of sortedAssistantStateSequence) {
      if (state.phase === 'idle') {
        continue;
      }
      if (state.phase === 'messaging') {
        continue;
      }
      if (state.phase === 'thinking' || state.phase === 'execution') {
        items.push({ type: 'state', state });
      }
    }

    items.sort((a, b) => {
      const tsA =
        a.type === 'message' ? a.message.timestamp : a.state.phaseStartedAt;
      const tsB =
        b.type === 'message' ? b.message.timestamp : b.state.phaseStartedAt;
      if (tsA !== tsB) {
        return tsA - tsB;
      }
      if (a.type === 'state' && b.type === 'message') {
        return isUserMessage(b.message) ? 1 : -1;
      }
      if (a.type === 'message' && b.type === 'state') {
        return isUserMessage(a.message) ? -1 : 1;
      }
      return 0;
    });

    return groupConversationTimelineItems(
      items,
      getActiveAssistantTurn(allMessages, currentAssistantState)
    );
  }, [allMessages, currentAssistantState, sortedAssistantStateSequence]);

  const activeTurnUserMessageId = useMemo(
    () =>
      getActiveAssistantTurn(allMessages, currentAssistantState).userMessageId,
    [allMessages, currentAssistantState]
  );

  return {
    allMessages,
    currentAssistantState,
    groupedMessages,
    activeTurnUserMessageId,
  };
}

export function conversationMessageGroupKey(
  group: ConversationMessageGroup,
  index: number
): string {
  if (group.type === 'user') {
    return userMessageGroupKey(group.message);
  }
  const firstItem = group.items[0];
  if (firstItem?.type === 'message') {
    return `assistant-message-${firstItem.message.id}`;
  }
  if (firstItem?.type === 'state') {
    return `assistant-state-${firstItem.state.sequence}`;
  }
  return `assistant-${group.userMessageId ?? index}`;
}

export const ConversationMessageGroupView = memo(
  ({
    group,
    index,
    totalGroups,
    lastAssistantIndex,
    allMessages,
    elementInspectionPresentations = {},
    currentAssistantStateSequence,
    activeTurnUserMessageId,
    isStreaming,
    renderToolMessage,
    assistantBubbleStyle = 'plain',
    showMessageIdentity = true,
    onSendMessage,
    onApproveLocationRequest,
    onConfirmOAuthAuthorizationRequest,
    onCompleteInteractiveLoginRequest,
    onDecideHostAccessRequest,
    onDecideComputerUseStartRequest,
    onAttachmentImageLayout,
  }: {
    group: ConversationMessageGroup;
    index: number;
    totalGroups: number;
    lastAssistantIndex: number;
    allMessages: ConversationMessage[];
    elementInspectionPresentations?: Record<
      string,
      ChatElementInspectionPresentationPayload
    >;
    currentAssistantStateSequence?: number;
    activeTurnUserMessageId?: string;
    isStreaming: boolean;
    renderToolMessage?: ToolMessageRenderer;
    assistantBubbleStyle?: AssistantBubbleStyle;
    showMessageIdentity?: boolean;
    onSendMessage?: (text: string) => void | Promise<void>;
    onApproveLocationRequest?: (requestId: string) => void;
    onConfirmOAuthAuthorizationRequest?: (
      requestId: string
    ) => void | Promise<void>;
    onCompleteInteractiveLoginRequest?: (
      requestId: string
    ) => void | Promise<void>;
    onDecideHostAccessRequest?: (
      requestId: string,
      decision: HostAccessDecisionRequest
    ) => void | Promise<void>;
    onDecideComputerUseStartRequest?: (
      requestId: string,
      decision: ComputerUseStartDecisionRequest
    ) => void | Promise<void>;
    onAttachmentImageLayout?: ConversationAttachmentImageLayoutHandler;
  }) => {
    switch (group.type) {
      case 'user':
        return (
          <ErrorBoundary key={userMessageGroupKey(group.message)}>
            <DebugMessage data={group}>
              <UserMessage
                message={group.message}
                elementInspectionPresentations={elementInspectionPresentations}
                enterAnimation={
                  isStreaming &&
                  index === totalGroups - 1 &&
                  isOptimisticLocalUserMessage(group.message)
                }
                showIdentity={showMessageIdentity}
                onAttachmentImageLayout={onAttachmentImageLayout}
                onSendMessage={onSendMessage}
              />
            </DebugMessage>
          </ErrorBoundary>
        );
      case 'assistant': {
        const isLastAssistant = index === lastAssistantIndex;
        const isActiveAssistantTurn = isStreaming && index === totalGroups - 1;
        const hideOperations = shouldHideAssistantOperations(
          group.userMessageId,
          activeTurnUserMessageId
        );

        return (
          <ErrorBoundary key={`assistant-${index}`}>
            <DebugMessage data={group}>
              <AssistantMessage
                items={group.items}
                allMessages={allMessages}
                assistantBubbleStyle={assistantBubbleStyle}
                currentAssistantStateSequence={currentAssistantStateSequence}
                userMessageId={group.userMessageId}
                isLast={isLastAssistant}
                isStreaming={isActiveAssistantTurn}
                isAnimating={isActiveAssistantTurn}
                showIdentity={showMessageIdentity}
                hideOperations={hideOperations}
                renderToolMessage={renderToolMessage}
                onSendMessage={onSendMessage}
                onApproveLocationRequest={onApproveLocationRequest}
                onConfirmOAuthAuthorizationRequest={
                  onConfirmOAuthAuthorizationRequest
                }
                onCompleteInteractiveLoginRequest={
                  onCompleteInteractiveLoginRequest
                }
                onDecideHostAccessRequest={onDecideHostAccessRequest}
                onDecideComputerUseStartRequest={
                  onDecideComputerUseStartRequest
                }
                onAttachmentImageLayout={onAttachmentImageLayout}
              />
            </DebugMessage>
          </ErrorBoundary>
        );
      }
      default:
        return null;
    }
  }
);

ConversationMessageGroupView.displayName = 'ConversationMessageGroupView';

export const ConversationThread = ({
  messages,
  elementInspectionPresentations = {},
  assistantStateSequence,
  isStreaming,
  className,
  renderToolMessage,
  assistantBubbleStyle = 'plain',
  showMessageIdentity = true,
  onSendMessage,
  onApproveLocationRequest,
  onConfirmOAuthAuthorizationRequest,
  onCompleteInteractiveLoginRequest,
  onDecideHostAccessRequest,
  onDecideComputerUseStartRequest,
  onAttachmentImageLayout,
}: {
  messages: ConversationMessage[];
  elementInspectionPresentations?: Record<
    string,
    ChatElementInspectionPresentationPayload
  >;
  assistantStateSequence: AssistantState[];
  isStreaming: boolean;
  className?: string;
  renderToolMessage?: ToolMessageRenderer;
  assistantBubbleStyle?: AssistantBubbleStyle;
  showMessageIdentity?: boolean;
  onSendMessage?: (text: string) => void | Promise<void>;
  onApproveLocationRequest?: (requestId: string) => void;
  onConfirmOAuthAuthorizationRequest?: (
    requestId: string
  ) => void | Promise<void>;
  onCompleteInteractiveLoginRequest?: (
    requestId: string
  ) => void | Promise<void>;
  onDecideHostAccessRequest?: (
    requestId: string,
    decision: HostAccessDecisionRequest
  ) => void | Promise<void>;
  onDecideComputerUseStartRequest?: (
    requestId: string,
    decision: ComputerUseStartDecisionRequest
  ) => void | Promise<void>;
  onAttachmentImageLayout?: ConversationAttachmentImageLayoutHandler;
}) => {
  const {
    allMessages,
    currentAssistantState,
    groupedMessages,
    activeTurnUserMessageId,
  } = useConversationMessageGroups({
    messages,
    assistantStateSequence,
  });

  const lastAssistantIndex = groupedMessages.findLastIndex(
    group => group.type === 'assistant'
  );
  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {groupedMessages.map((group, index) => (
        <ConversationMessageGroupView
          key={conversationMessageGroupKey(group, index)}
          group={group}
          index={index}
          totalGroups={groupedMessages.length}
          lastAssistantIndex={lastAssistantIndex}
          allMessages={allMessages}
          elementInspectionPresentations={elementInspectionPresentations}
          currentAssistantStateSequence={currentAssistantState?.sequence}
          activeTurnUserMessageId={activeTurnUserMessageId}
          isStreaming={isStreaming}
          renderToolMessage={renderToolMessage}
          assistantBubbleStyle={assistantBubbleStyle}
          showMessageIdentity={showMessageIdentity}
          onSendMessage={onSendMessage}
          onApproveLocationRequest={onApproveLocationRequest}
          onConfirmOAuthAuthorizationRequest={
            onConfirmOAuthAuthorizationRequest
          }
          onCompleteInteractiveLoginRequest={onCompleteInteractiveLoginRequest}
          onDecideHostAccessRequest={onDecideHostAccessRequest}
          onDecideComputerUseStartRequest={onDecideComputerUseStartRequest}
          onAttachmentImageLayout={onAttachmentImageLayout}
        />
      ))}
    </div>
  );
};
